import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoginForm from './components/LoginForm';
import LoginHeader from './components/LoginHeader';
import RoleHints from './components/RoleHints';
import SecurityIndicators from './components/SecurityIndicators';
import ErrorBoundary from '../../components/ErrorBoundary';
import Button from '../../components/ui/Button';
import Icon from './components/Icon';

const sanitizeNext = (nextRaw) => {
  if (!nextRaw) return '/admin-dashboard';
  try {
    // Disallow absolute URLs (open redirect protection)
    if (/^https?:\/\//i?.test(nextRaw)) return '/admin-dashboard';

    // Normalize to ensure it starts with a slash
    let next = nextRaw?.startsWith('/') ? nextRaw : `/${nextRaw}`;

    // Basic allowlist: keep users inside your app
    const allowedPrefixes = ['/', '/app', '/admin-dashboard', '/pricing', '/onboarding', '/login'];
    const ok = allowedPrefixes?.some((p) => next === p || next?.startsWith(`${p}/`));
    return ok ? next : '/admin-dashboard';
  } catch {
    return '/admin-dashboard';
  }
};

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { 
    user, 
    loading, 
    connectionError, 
    retryConnection, 
    bypassMode, 
    enableBypassMode 
  } = useAuth();
  const [error, setError] = useState('');
  const [isRetrying, setIsRetrying] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  // pick up auth errors from providers (?error= / ?error_description=)
  useEffect(() => {
    const qpErr = searchParams?.get('error_description') || searchParams?.get('error');
    if (qpErr) setError(qpErr);
  }, [searchParams]);

  // Compute a clean return URL (prefer ?next, then location.state.from, else /admin-dashboard)
  const returnUrl = useMemo(() => {
    const nextParam = searchParams?.get('next');
    const stateFrom = location?.state?.from?.pathname || '';
    return sanitizeNext(nextParam || stateFrom || '/admin-dashboard');
  }, [searchParams, location?.state]);

  // If already authenticated, redirect to the return URL
  useEffect(() => {
    if (!loading && user) {
      navigate(returnUrl, { replace: true });
    }
  }, [user, loading, navigate, returnUrl]);

  // Enhanced connection error handler with bypass option
  const handleRetryConnection = async () => {
    setIsRetrying(true);
    setError('');
    
    try {
      const success = await retryConnection();
      if (success) {
        setError('');
      } else {
        setError('Connection retry failed. You can use bypass mode for immediate access.');
      }
    } catch (err) {
      console.error('Retry connection error:', err);
      setError('Failed to reconnect. You can use bypass mode for immediate access.');
    } finally {
      setIsRetrying(false);
    }
  };

  // Bypass mode handler
  const handleBypassMode = () => {
    enableBypassMode();
    navigate(returnUrl, { replace: true });
  };

  // Dark mode toggle
  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    document.documentElement?.classList?.toggle('dark');
  };

  // Enhanced loading state with branded design
  if (loading && !bypassMode) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50 to-purple-50 flex items-center justify-center">
        <div className="text-center animate-fade-in">
          <div className="relative mb-8">
            <div className="animate-spin rounded-full h-20 w-20 border-4 border-indigo-100 border-t-indigo-500 mx-auto" />
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 opacity-20 animate-pulse"></div>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-center space-x-2 mb-4">
              <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                <Icon name="Clock" size={20} className="text-white" />
              </div>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                Intime ATS
              </h2>
            </div>
            <p className="text-slate-700 font-semibold text-lg">Initializing your workspace...</p>
            <p className="text-sm text-slate-500 max-w-xs mx-auto">
              Connecting to secure authentication services and preparing your dashboard
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Don't flash the form if we already have a user
  if (user) return null;

  const handleLoginSuccess = () => {
    navigate(returnUrl, { replace: true });
  };

  return (
    <div className={`min-h-screen relative overflow-hidden transition-all duration-500 ${
      isDarkMode 
        ? 'bg-gradient-to-br from-slate-900 via-indigo-900 to-purple-900' :'bg-gradient-to-br from-slate-50 via-indigo-50 to-purple-50'
    }`}>
      {/* Enhanced Background Elements */}
      <div className="absolute inset-0">
        {/* Floating gradient orbs with improved animations */}
        <div className="absolute top-0 -left-4 w-96 h-96 bg-gradient-to-r from-indigo-400/30 to-purple-400/30 rounded-full mix-blend-multiply filter blur-2xl opacity-70 animate-float"></div>
        <div className="absolute top-0 -right-4 w-96 h-96 bg-gradient-to-r from-purple-400/30 to-pink-400/30 rounded-full mix-blend-multiply filter blur-2xl opacity-70 animate-float" style={{animationDelay: '2s'}}></div>
        <div className="absolute -bottom-8 left-20 w-96 h-96 bg-gradient-to-r from-blue-400/30 to-indigo-400/30 rounded-full mix-blend-multiply filter blur-2xl opacity-70 animate-float" style={{animationDelay: '4s'}}></div>
        
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%236366f1%22%20fill-opacity%3D%220.03%22%3E%3Ccircle%20cx%3D%227%22%20cy%3D%227%22%20r%3D%221%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-40"></div>
      </div>

      {/* Dark Mode Toggle */}
      <div className="absolute top-6 right-6 z-20">
        <button
          onClick={toggleDarkMode}
          className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center text-white hover:bg-white/20 transition-all duration-200 hover:scale-105"
        >
          <Icon name={isDarkMode ? "Sun" : "Moon"} size={20} />
        </button>
      </div>

      <ErrorBoundary>
        <div className="relative z-10 flex min-h-screen">
          <div className="w-full max-w-7xl mx-auto flex flex-col lg:flex-row">
            {/* Left Panel - Enhanced with Better Proportions (55%) */}
            <div className="lg:w-[55%] p-8 lg:p-16 flex flex-col justify-center relative">
              {/* Enhanced gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/95 via-indigo-700/95 to-purple-800/95 backdrop-blur-sm"></div>
              
              {/* Subtle animated pattern overlay */}
              <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2280%22%20height%3D%2280%22%20viewBox%3D%220%200%2080%2080%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.05%22%3E%3Cpath%20d%3D%22M0%200h80v80H0V0zm20%2020v40h40V20H20zm20%2035a15%2015%200%201%201%200-30%2015%2015%200%200%201%200%2030z%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20 animate-pulse"></div>
              
              <div className="relative z-10 text-white animate-slide-up">
                <LoginHeader />
                
                {/* Enhanced Feature Cards with Glass Morphism */}
                <div className="mt-16 space-y-6">
                  <div className="group p-6 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 hover:bg-white/15 transition-all duration-300 hover:transform hover:scale-105">
                    <div className="flex items-start space-x-4">
                      <div className="w-14 h-14 bg-gradient-to-r from-white/20 to-white/30 rounded-2xl flex items-center justify-center group-hover:from-white/30 group-hover:to-white/40 transition-all duration-300">
                        <Icon name="Users" size={28} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-xl text-white mb-2">Smart Talent Management</h3>
                        <p className="text-white/80 text-sm leading-relaxed">
                          Streamline your recruitment process with AI-powered candidate matching, automated screening, and intelligent pipeline management.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="group p-6 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 hover:bg-white/15 transition-all duration-300 hover:transform hover:scale-105" style={{animationDelay: '0.1s'}}>
                    <div className="flex items-start space-x-4">
                      <div className="w-14 h-14 bg-gradient-to-r from-white/20 to-white/30 rounded-2xl flex items-center justify-center group-hover:from-white/30 group-hover:to-white/40 transition-all duration-300">
                        <Icon name="BarChart3" size={28} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-xl text-white mb-2">Advanced Analytics & Insights</h3>
                        <p className="text-white/80 text-sm leading-relaxed">
                          Make data-driven hiring decisions with comprehensive analytics, performance metrics, and predictive insights.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="group p-6 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 hover:bg-white/15 transition-all duration-300 hover:transform hover:scale-105" style={{animationDelay: '0.2s'}}>
                    <div className="flex items-start space-x-4">
                      <div className="w-14 h-14 bg-gradient-to-r from-white/20 to-white/30 rounded-2xl flex items-center justify-center group-hover:from-white/30 group-hover:to-white/40 transition-all duration-300">
                        <Icon name="Shield" size={28} className="text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-xl text-white mb-2">Enterprise Security & Compliance</h3>
                        <p className="text-white/80 text-sm leading-relaxed">
                          Bank-level security with SOC 2 Type II compliance, GDPR compliance, and advanced data protection.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Trust Indicators */}
                <div className="mt-12 pt-8 border-t border-white/20">
                  <div className="flex items-center justify-center space-x-8 text-white/60">
                    <div className="flex items-center space-x-2">
                      <Icon name="Shield" size={16} />
                      <span className="text-sm font-medium">SOC 2 Certified</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="Lock" size={16} />
                      <span className="text-sm font-medium">GDPR Compliant</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Icon name="Star" size={16} />
                      <span className="text-sm font-medium">99.9% Uptime</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Panel - Login Form (45%) */}
            <div className="lg:w-[45%] p-8 lg:p-16 flex flex-col justify-center">
              <div className="max-w-lg mx-auto w-full animate-slide-up" style={{animationDelay: '0.3s'}}>
                {/* Enhanced Glass Card Container */}
                <div className="bg-white/90 backdrop-blur-xl rounded-3xl shadow-2xl p-8 lg:p-12 border border-white/30 relative overflow-hidden">
                  {/* Subtle animated background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/50 via-transparent to-purple-50/50 opacity-50"></div>
                  
                  <div className="relative z-10">
                    {/* Enhanced Header */}
                    <div className="text-center mb-12">
                      <div className="flex items-center justify-center mb-6">
                        <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                          <Icon name="Clock" size={24} className="text-white" />
                        </div>
                      </div>
                      <h1 className="text-4xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent mb-3 tracking-tight">
                        Welcome Back
                      </h1>
                      <p className="text-slate-600 text-lg font-medium">Sign in to your Intime ATS workspace</p>
                    </div>

                    {/* Status Indicators */}
                    {/* Bypass Mode Indicator */}
                    {bypassMode && (
                      <div className="mb-6 p-4 bg-orange-50/80 backdrop-blur-sm border border-orange-200/50 rounded-2xl">
                        <div className="flex items-center space-x-3">
                          <Icon name="Shield" size={20} className="text-orange-600 flex-shrink-0" />
                          <div>
                            <p className="text-sm font-semibold text-orange-800">Bypass Mode Active</p>
                            <p className="text-xs text-orange-600">Temporary access granted - enhanced security disabled</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Enhanced Connection Error Handler */}
                    {connectionError && !bypassMode && (
                      <div className="mb-6 p-6 bg-red-50/80 backdrop-blur-sm border border-red-200/50 rounded-2xl">
                        <div className="flex items-start space-x-4">
                          <Icon name="AlertCircle" size={24} className="text-red-600 mt-1 flex-shrink-0" />
                          <div className="flex-1">
                            <h4 className="text-base font-semibold text-red-800 mb-2">Service Connection Error</h4>
                            <p className="text-sm text-red-600 mb-4">{connectionError}</p>
                            
                            <div className="space-y-4">
                              <div className="flex flex-wrap gap-3">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={handleRetryConnection}
                                  disabled={isRetrying}
                                  className="border-red-300 text-red-700 hover:bg-red-50 focus:ring-red-500"
                                >
                                  {isRetrying ? (
                                    <>
                                      <div className="animate-spin w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full mr-2"></div>
                                      Retrying...
                                    </>
                                  ) : (
                                    <>
                                      <Icon name="RefreshCw" size={16} className="mr-2" />
                                      Retry Connection
                                    </>
                                  )}
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => window.location?.reload()}
                                  className="border-red-300 text-red-700 hover:bg-red-50 focus:ring-red-500"
                                >
                                  <Icon name="RotateCcw" size={16} className="mr-2" />
                                  Refresh Page
                                </Button>
                              </div>

                              <div className="pt-4 border-t border-red-200/50">
                                <p className="text-sm text-red-600 mb-3">
                                  <strong>Need immediate access?</strong> Use bypass mode for temporary access while services are restored.
                                </p>
                                <Button
                                  onClick={handleBypassMode}
                                  size="sm"
                                  className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                                >
                                  <Icon name="Shield" size={16} className="mr-2" />
                                  Enable Bypass Mode
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Service Status - Success */}
                    {!connectionError && !bypassMode && (
                      <div className="mb-6 p-4 bg-green-50/80 backdrop-blur-sm border border-green-200/50 rounded-2xl">
                        <div className="flex items-center space-x-3">
                          <Icon name="CheckCircle" size={20} className="text-green-600 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-green-700">All systems operational</p>
                            <p className="text-xs text-green-600">Secure authentication services connected</p>
                          </div>
                          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                        </div>
                      </div>
                    )}

                    {/* Login Form */}
                    {(!connectionError || bypassMode) && !loading && (
                      <LoginForm 
                        onSuccess={handleLoginSuccess}
                        error={error}
                        setError={setError}
                      />
                    )}

                    {/* Enhanced Footer Information */}
                    {!connectionError && !loading && !bypassMode && (
                      <div className="mt-12 space-y-6">
                        <RoleHints />
                        <SecurityIndicators />
                      </div>
                    )}

                    {/* Bypass Mode Instructions */}
                    {bypassMode && (
                      <div className="mt-8 p-6 bg-blue-50/80 backdrop-blur-sm border border-blue-200/50 rounded-2xl">
                        <div className="text-center">
                          <Icon name="Info" size={24} className="text-blue-600 mx-auto mb-3" />
                          <p className="text-base font-semibold text-blue-800 mb-2">Bypass Mode Instructions</p>
                          <p className="text-sm text-blue-600 mb-1">You can use any valid credentials to sign in</p>
                          <p className="text-xs text-blue-500">Temporary access mode - standard security protocols are relaxed</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Enhanced Footer Branding */}
                <div className="mt-8 text-center">
                  <p className="text-sm text-slate-500 mb-2">
                    © 2024 Intime ATS. Streamlining talent acquisition with intelligent solutions.
                  </p>
                  <div className="flex items-center justify-center space-x-4 text-xs text-slate-400">
                    <span>Privacy Policy</span>
                    <span>•</span>
                    <span>Terms of Service</span>
                    <span>•</span>
                    <span>Support</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ErrorBoundary>
    </div>
  );
};

export default LoginPage;